import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";

interface HomeStats {
  instructorCount: number;
  studentCount: number;
  avgRating: number | null;
}

export function useHomeStats() {
  // Defer stats fetching by 2s so it doesn't compete with critical rendering
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  return useQuery({
    queryKey: ["home-stats"],
    enabled: ready,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<HomeStats> => {
      const [instructorsRes, studentsRes, ratingsRes] = await Promise.all([
        supabase
          .from("classes")
          .select("instructor_id", { count: "exact", head: true })
          .eq("status", "published")
          .not("instructor_id", "is", null),
        supabase
          .from("bookings")
          .select("learner_id", { count: "exact", head: true }),
        supabase
          .from("reviews")
          .select("rating"),
      ]);

      const instructorCount = instructorsRes.count ?? 0;
      const studentCount = studentsRes.count ?? 0;

      let avgRating: number | null = null;
      if (ratingsRes.data && ratingsRes.data.length > 0) {
        const sum = ratingsRes.data.reduce((acc, r) => acc + r.rating, 0);
        avgRating = Math.round((sum / ratingsRes.data.length) * 10) / 10;
      }

      return { instructorCount, studentCount, avgRating };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
